import {ViewIdentifiable} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {ApiReturnType, getUserViewSettingsApiRequest} from "../../models/common/ApiReturnType";
import {AppState} from "../../reducers/RootReducer";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import {
    ExternalWorkGroup,
    ExternalWorkGroupId,
    ExternalWorkGroupProp,
    ExternalWorkGroupModule,
    ExternalWorkGroupRequest,
    ExternalWorkGroupRequestProp
} from "../../models/contractor/ExternalWorkGroup";

import {  ofType, StateObservable } from "redux-observable";
import { switchMap, map, catchError } from "rxjs/operators";
import { ajax } from "rxjs/ajax";
import { Observable, of } from 'rxjs';
import {Nullable} from "../../models/common/Nullable";
import {Identifier, makeNumericId} from "../../models/common/Identifier";
import {RouteConfig, RouteMethods} from "../../config/RouteConfig";
import {sendUserToPage} from "../common/UserViewSettingsActions";

/**
 * base API path to interact with project roles
 */
function apiPath( id: Nullable<number> = null ): string {
    return APP_CONFIG.baseWeb + "external-work/groups/"+ ( id !== null ? id : '');
}

/** union of all project role actions types */
export type ExternalWorkGroupActionTypes = FetchExternalWorkGroupsActionTypes
    | FetchExternalWorkGroupByIdActionTypes
    | CreateExternalWorkGroupActionTypes
    | UpdateExternalWorkGroupActionTypes
    | DeleteExternalWorkGroupActionTypes
    | RestoreExternalWorkGroupActionTypes

/**
 * FETCH EXTERNAL_WORK_GROUPS BY USER VIEW SETTINGS
 *
 * user view settings will automatically be retrieved from redux store
 */
// TYPESCRIPT REDUX ACTION TYPES
export interface FetchExternalWorkGroupsAction extends ViewIdentifiable, ReduxAction< typeof FETCH_EXTERNAL_WORK_GROUPS > {}
export interface FetchExternalWorkGroupsSuccessAction extends ViewIdentifiable, ReduxAction<typeof FETCH_EXTERNAL_WORK_GROUPS_SUCCESS > {
    payload: ApiReturnType< ExternalWorkGroup[] >
}
export interface FetchExternalWorkGroupsErrorAction extends ViewIdentifiable, ErrorAction< typeof FETCH_EXTERNAL_WORK_GROUPS_ERROR >{}

export type FetchExternalWorkGroupsActionTypes = FetchExternalWorkGroupsAction
    | FetchExternalWorkGroupsErrorAction
    | FetchExternalWorkGroupsSuccessAction

// REDUX ACTION TYPE CONSTANTS
export const FETCH_EXTERNAL_WORK_GROUPS = "FETCH_EXTERNAL_WORK_GROUPS";
export const FETCH_EXTERNAL_WORK_GROUPS_SUCCESS = "FETCH_EXTERNAL_WORK_GROUPS_SUCCESS";
export const FETCH_EXTERNAL_WORK_GROUPS_ERROR = "FETCH_EXTERNAL_WORK_GROUPS_ERROR";

// REDUX ACTION ACCESSORS FUNCTIONS
export function fetchExternalWorkGroups( viewIdentifier: string ) : FetchExternalWorkGroupsAction {
    return { type: FETCH_EXTERNAL_WORK_GROUPS, viewIdentifier };
}
export function fetchExternalWorkGroupsSuccess(
    viewIdentifier: string,
    payload: ApiReturnType< ExternalWorkGroup[] >
) : FetchExternalWorkGroupsSuccessAction
{
    return { type: FETCH_EXTERNAL_WORK_GROUPS_SUCCESS, viewIdentifier, payload };
}
export function fetchExternalWorkGroupsError( viewIdentifier: string, error: Error ): FetchExternalWorkGroupsErrorAction {
    return { type: FETCH_EXTERNAL_WORK_GROUPS_ERROR, viewIdentifier, error };
}

// REDUX OBSERVABLE EPIC
export function fetchExternalWorkGroupsEpic(
    action$: Observable< FetchExternalWorkGroupsAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_EXTERNAL_WORK_GROUPS ),
        switchMap( action => {
            const request = getUserViewSettingsApiRequest(
                apiPath(),
                action.viewIdentifier,
                store$
            );

            return ajax( APP_CONFIG.getRequestObject(request.path) ).pipe(
                map( response => fetchExternalWorkGroupsSuccess(
                    action.viewIdentifier,
                    response.response as ApiReturnType<ExternalWorkGroup[]>
                ) ),
                catchError(error => of(
                    fetchExternalWorkGroupsError(action.viewIdentifier, error),
                    eventNotificationError(error, store$, action.viewIdentifier, "fetchExternalWorkGroups")
                ))
            );

        })
    );
}

/**
 * FETCH SINGLE EXTERNAL_WORK_GROUP
 */
export interface FetchExternalWorkGroupByIdAction extends ExternalWorkGroupId, ReduxAction< typeof FETCH_EXTERNAL_WORK_GROUP_BY_ID> {

}
export interface FetchExternalWorkGroupByIdSuccessAction extends ExternalWorkGroupProp, ReduxAction< typeof FETCH_EXTERNAL_WORK_GROUP_BY_ID_SUCCESS> {

}
export interface FetchExternalWorkGroupByIdErrorAction extends ExternalWorkGroupId, ErrorAction< typeof FETCH_EXTERNAL_WORK_GROUP_BY_ID_ERROR> {

}
//union of action to fetch by id
export type FetchExternalWorkGroupByIdActionTypes = FetchExternalWorkGroupByIdAction
    | FetchExternalWorkGroupByIdSuccessAction
    | FetchExternalWorkGroupByIdErrorAction

// REDUX ACTION TYPE CONSTANTS
export const FETCH_EXTERNAL_WORK_GROUP_BY_ID = "FETCH_EXTERNAL_WORK_GROUP_BY_ID";
export const FETCH_EXTERNAL_WORK_GROUP_BY_ID_SUCCESS = "FETCH_EXTERNAL_WORK_GROUP_BY_ID_SUCCESS";
export const FETCH_EXTERNAL_WORK_GROUP_BY_ID_ERROR = "FETCH_EXTERNAL_WORK_GROUP_BY_ID_ERROR";

//Redux action accessors function
export function fetchExternalWorkGroupById(externalWorkGroupId: Identifier<number> ): FetchExternalWorkGroupByIdAction {
    return { type: FETCH_EXTERNAL_WORK_GROUP_BY_ID, externalWorkGroupId };
}
export function fetchExternalWorkGroupByIdSuccess( externalWorkGroup: ExternalWorkGroup ): FetchExternalWorkGroupByIdSuccessAction {
    return { type: FETCH_EXTERNAL_WORK_GROUP_BY_ID_SUCCESS, externalWorkGroup };
}
export function fetchExternalWorkGroupByIdError( error: any, externalWorkGroupId: Identifier<number> ): FetchExternalWorkGroupByIdErrorAction {
    return { type: FETCH_EXTERNAL_WORK_GROUP_BY_ID_ERROR, error, externalWorkGroupId };
}

//redux observable epic as bridge to rxjs
export function fetchExternalWorkGroupByIdEpic(
    action$: Observable< FetchExternalWorkGroupByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_EXTERNAL_WORK_GROUP_BY_ID ),
        switchMap( action => {
                return ajax(
                    APP_CONFIG.getRequestObject( apiPath( action.externalWorkGroupId.rawValue ) )
                ).pipe(
                    map( response => fetchExternalWorkGroupByIdSuccess( response.response as ExternalWorkGroup ) ),
                    catchError( error => of(
                        fetchExternalWorkGroupByIdError( error, action.externalWorkGroupId ),
                        eventNotificationError( error, store$, ExternalWorkGroupModule.identifier, "fetchExternalWorkGroupById" )
                    ) )
                )
            }
        )
    );
}

/**
 * MUTATING REQUESTS
 */


/**
 * CREATE EXTERNAL_WORK_GROUP
 */
export interface CreateExternalWorkGroupAction extends ExternalWorkGroupRequestProp, ReduxAction<typeof CREATE_EXTERNAL_WORK_GROUP> {

}
export interface CreateExternalWorkGroupSuccessAction extends ExternalWorkGroupId, ReduxAction<typeof CREATE_EXTERNAL_WORK_GROUP_SUCCESS> {

}
export interface CreateExternalWorkGroupErrorAction extends ErrorAction<typeof CREATE_EXTERNAL_WORK_GROUP_ERROR> {

}
// typescript union type of create actions
export type CreateExternalWorkGroupActionTypes = CreateExternalWorkGroupAction
    | CreateExternalWorkGroupSuccessAction
    | CreateExternalWorkGroupErrorAction

//redux action constants
export const CREATE_EXTERNAL_WORK_GROUP = "CREATE_EXTERNAL_WORK_GROUP";
export const CREATE_EXTERNAL_WORK_GROUP_SUCCESS = "CREATE_EXTERNAL_WORK_GROUP_SUCCESS";
export const CREATE_EXTERNAL_WORK_GROUP_ERROR = "CREATE_EXTERNAL_WORK_GROUP_ERROR";

//redux action accessors functions
export function createExternalWorkGroup( externalWorkGroup: ExternalWorkGroupRequest ): CreateExternalWorkGroupAction {
    return { type: CREATE_EXTERNAL_WORK_GROUP,  externalWorkGroup };
}
export function createExternalWorkGroupSuccess( externalWorkGroupId: Identifier<number> ): CreateExternalWorkGroupSuccessAction {
    return { type: CREATE_EXTERNAL_WORK_GROUP_SUCCESS, externalWorkGroupId };
}
export function createExternalWorkGroupError( error: any ): CreateExternalWorkGroupErrorAction {
    return { type: CREATE_EXTERNAL_WORK_GROUP_ERROR, error };
}

//redux observable rxjs bridge
export function createExternalWorkGroupEpic(
    action$: Observable< CreateExternalWorkGroupAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( CREATE_EXTERNAL_WORK_GROUP ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath(),
                    RouteMethods.post,
                    action.externalWorkGroup
                )
            ).pipe(
                switchMap( response => of(
                    createExternalWorkGroupSuccess( makeNumericId( action.externalWorkGroup.externalWorkGroupId ) ),
                    eventNotificationSuccess(),
                    fetchExternalWorkGroups( ExternalWorkGroupModule.identifier ),
                    sendUserToPage(
                        RouteConfig.externalWorkGroups.routes.main.path,
                        ExternalWorkGroupModule.identifier
                    )
                ) ),
                catchError( error => of(
                    createExternalWorkGroupError( error ),
                    eventNotificationError( error, store$, ExternalWorkGroupModule.identifier, "createExternalWorkGroup" )
                ))
            )
        )
    )
}

/**
 * UPDATE EXTERNAL_WORK_GROUP
 */
export interface UpdateExternalWorkGroupAction extends ExternalWorkGroupRequestProp, ReduxAction<typeof UPDATE_EXTERNAL_WORK_GROUP> {

}
export interface UpdateExternalWorkGroupSuccessAction extends ExternalWorkGroupId, ReduxAction<typeof UPDATE_EXTERNAL_WORK_GROUP_SUCCESS> {

}
export interface UpdateExternalWorkGroupErrorAction extends ErrorAction<typeof UPDATE_EXTERNAL_WORK_GROUP_ERROR> {

}
// typescript union type of update actions
export type UpdateExternalWorkGroupActionTypes = UpdateExternalWorkGroupAction
    | UpdateExternalWorkGroupSuccessAction
    | UpdateExternalWorkGroupErrorAction

//redux action constants
export const UPDATE_EXTERNAL_WORK_GROUP = "UPDATE_EXTERNAL_WORK_GROUP";
export const UPDATE_EXTERNAL_WORK_GROUP_SUCCESS = "UPDATE_EXTERNAL_WORK_GROUP_SUCCESS";
export const UPDATE_EXTERNAL_WORK_GROUP_ERROR = "UPDATE_EXTERNAL_WORK_GROUP_ERROR";

//redux action accessors functions
export function updateExternalWorkGroup( externalWorkGroup: ExternalWorkGroupRequest ): UpdateExternalWorkGroupAction {
    return { type: UPDATE_EXTERNAL_WORK_GROUP, externalWorkGroup };
}
export function updateExternalWorkGroupSuccess( externalWorkGroupId: Identifier<number> ): UpdateExternalWorkGroupSuccessAction {
    return { type: UPDATE_EXTERNAL_WORK_GROUP_SUCCESS, externalWorkGroupId };
}
export function updateExternalWorkGroupError( error: any ): UpdateExternalWorkGroupErrorAction {
    return { type: UPDATE_EXTERNAL_WORK_GROUP_ERROR, error };
}

//redux observable rxjs bridge
export function updateExternalWorkGroupEpic(
    action$: Observable< UpdateExternalWorkGroupAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( UPDATE_EXTERNAL_WORK_GROUP ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath(),
                    RouteMethods.put,
                    action.externalWorkGroup
                )
            ).pipe(
                switchMap( response => of(
                    updateExternalWorkGroupSuccess( makeNumericId( action.externalWorkGroup.externalWorkGroupId ) ),
                    eventNotificationSuccess(),
                    fetchExternalWorkGroups( ExternalWorkGroupModule.identifier ),
                    fetchExternalWorkGroupById( makeNumericId( action.externalWorkGroup.externalWorkGroupId )),
                    sendUserToPage(
                        RouteConfig.externalWorkGroups.routes.main.path,
                        ExternalWorkGroupModule.identifier
                    )
                ) ),
                catchError( error => of(
                    updateExternalWorkGroupError( error ),
                    eventNotificationError( error, store$, ExternalWorkGroupModule.identifier, "updateExternalWorkGroup" )
                ))
            )
        )
    )
}

/**
 * DELETE EXTERNAL_WORK_GROUP
 */
export interface DeleteExternalWorkGroupAction extends ExternalWorkGroupId, ReduxAction<typeof DELETE_EXTERNAL_WORK_GROUP> {

}
export interface DeleteExternalWorkGroupSuccessAction extends ExternalWorkGroupId, ReduxAction<typeof DELETE_EXTERNAL_WORK_GROUP_SUCCESS> {

}
export interface DeleteExternalWorkGroupErrorAction extends ErrorAction<typeof DELETE_EXTERNAL_WORK_GROUP_ERROR> {

}
// typescript union type of delete actions
export type DeleteExternalWorkGroupActionTypes = DeleteExternalWorkGroupAction
    | DeleteExternalWorkGroupSuccessAction
    | DeleteExternalWorkGroupErrorAction

//redux action constants
export const DELETE_EXTERNAL_WORK_GROUP = "DELETE_EXTERNAL_WORK_GROUP";
export const DELETE_EXTERNAL_WORK_GROUP_SUCCESS = "DELETE_EXTERNAL_WORK_GROUP_SUCCESS";
export const DELETE_EXTERNAL_WORK_GROUP_ERROR = "DELETE_EXTERNAL_WORK_GROUP_ERROR";

//redux action accessors functions
export function deleteExternalWorkGroup( externalWorkGroupId: Identifier<number>  ): DeleteExternalWorkGroupAction {
    return { type: DELETE_EXTERNAL_WORK_GROUP, externalWorkGroupId };
}
export function deleteExternalWorkGroupSuccess( externalWorkGroupId: Identifier<number> ): DeleteExternalWorkGroupSuccessAction {
    return { type: DELETE_EXTERNAL_WORK_GROUP_SUCCESS, externalWorkGroupId };
}
export function deleteExternalWorkGroupError( error: any ): DeleteExternalWorkGroupErrorAction {
    return { type: DELETE_EXTERNAL_WORK_GROUP_ERROR, error };
}

//redux observable rxjs bridge
export function deleteExternalWorkGroupEpic(
    action$: Observable< DeleteExternalWorkGroupAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( DELETE_EXTERNAL_WORK_GROUP ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.externalWorkGroupId.rawValue ),
                    RouteMethods.delete
                )
            ).pipe(
                switchMap( _ => of(
                    deleteExternalWorkGroupSuccess( action.externalWorkGroupId ),
                    eventNotificationSuccess(),
                    fetchExternalWorkGroups( ExternalWorkGroupModule.identifier ),
                    fetchExternalWorkGroupById( action.externalWorkGroupId ),
                    sendUserToPage(
                        RouteConfig.externalWorkGroups.routes.main.path,
                        ExternalWorkGroupModule.identifier
                    )
                ) ),
                catchError( error => of(
                    deleteExternalWorkGroupError( error ),
                    eventNotificationError( error, store$, ExternalWorkGroupModule.identifier, "deleteExternalWorkGroup" )
                ))
            )
        )
    )
}

/**
 * RESTORE EXTERNAL_WORK_GROUP
 */
export interface RestoreExternalWorkGroupAction extends ExternalWorkGroupId, ReduxAction<typeof RESTORE_EXTERNAL_WORK_GROUP> {

}
export interface RestoreExternalWorkGroupSuccessAction extends ExternalWorkGroupId, ReduxAction<typeof RESTORE_EXTERNAL_WORK_GROUP_SUCCESS> {

}
export interface RestoreExternalWorkGroupErrorAction extends ErrorAction<typeof RESTORE_EXTERNAL_WORK_GROUP_ERROR> {

}
// typescript union type of restore actions
export type RestoreExternalWorkGroupActionTypes = RestoreExternalWorkGroupAction
    | RestoreExternalWorkGroupSuccessAction
    | RestoreExternalWorkGroupErrorAction

//redux action constants
export const RESTORE_EXTERNAL_WORK_GROUP = "RESTORE_EXTERNAL_WORK_GROUP";
export const RESTORE_EXTERNAL_WORK_GROUP_SUCCESS = "RESTORE_EXTERNAL_WORK_GROUP_SUCCESS";
export const RESTORE_EXTERNAL_WORK_GROUP_ERROR = "RESTORE_EXTERNAL_WORK_GROUP_ERROR";

//redux action accessors functions
export function restoreExternalWorkGroup( externalWorkGroupId: Identifier<number>  ): RestoreExternalWorkGroupAction {
    return { type: RESTORE_EXTERNAL_WORK_GROUP, externalWorkGroupId };
}
export function restoreExternalWorkGroupSuccess( externalWorkGroupId: Identifier<number> ): RestoreExternalWorkGroupSuccessAction {
    return { type: RESTORE_EXTERNAL_WORK_GROUP_SUCCESS, externalWorkGroupId };
}
export function restoreExternalWorkGroupError( error: any ): RestoreExternalWorkGroupErrorAction {
    return { type: RESTORE_EXTERNAL_WORK_GROUP_ERROR, error };
}

//redux observable rxjs bridge
export function restoreExternalWorkGroupEpic(
    action$: Observable< RestoreExternalWorkGroupAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( RESTORE_EXTERNAL_WORK_GROUP ),
        switchMap( action =>
            ajax(
                APP_CONFIG.getRequestObject(
                    apiPath( action.externalWorkGroupId.rawValue ),
                    RouteMethods.patch
                )
            ).pipe(
                switchMap( _ => of(
                    restoreExternalWorkGroupSuccess( action.externalWorkGroupId ),
                    eventNotificationSuccess(),
                    fetchExternalWorkGroups( ExternalWorkGroupModule.identifier ),
                    fetchExternalWorkGroupById( action.externalWorkGroupId ),
                    sendUserToPage(
                        RouteConfig.externalWorkGroups.routes.main.path,
                        ExternalWorkGroupModule.identifier
                    )
                ) ),
                catchError( error => of(
                    restoreExternalWorkGroupError( error ),
                    eventNotificationError( error, store$, ExternalWorkGroupModule.identifier, "restoreExternalWorkGroup" )
                ))
            )
        )
    )
}
